/**
 * Keeps a bot fleet out of Plausible.
 *
 * Since September 2026 a fleet loads one `/product/` page per session through
 * residential and mobile proxies, then its page JS reports a spoofed in-app
 * `navigator.userAgent`: Snapchat, DaumApps, NAVER, NewsSapphire, WeChat, or
 * `iPhone OS 15_7_2` paired with Safari `Version/16.5`, which never shipped
 * together (iOS 15.7.2 alone is a real version on older iPhones). It also
 * reports two bare browsers with no app label: an Android in-app browser whose
 * `wv` token is stripped, leaving `Build/…; )` (real Android WebViews say
 * `; wv)`), and iOS 18.3 Safari ending `Safari/605.1` where real iPhones say
 * `Safari/604.1`. Plausible
 * files these as "Mobile App / Direct / None" and counted ~9,000 sessions a
 * day, about a third of all
 * product-page sessions. They never click out: 0 retailer clicks from 8,981
 * sessions in a day, against ~3% for other product-page sessions. Cloudflare
 * can't block them without challenging real shoppers, and Plausible has no
 * user-agent filter, so we stop reporting them here instead.
 *
 * Cost: before the fleet arrived these labels were ~55 sessions a week
 * (~0.01% of traffic), so a handful of real in-app visitors are dropped too.
 * The two bare patterns matched 22 sessions in all of August (4 clicked out)
 * against ~5,500 a day with one click-out once the Android wave arrived.
 * Narrowed in October 2026, when real visitors started matching two patterns:
 * real Snapchat (14.x) puts no `Safari/604.1` before its app token, and real
 * Android in-app browsers that also drop `wv` (Instagram, FB_IAB, Bing, Samsung)
 * run Chrome 150+, which the fleet almost never reports. Signed-in users in
 * both groups clicked out. If we ever buy Snapchat,
 * KakaoTalk/Daum, NAVER or WeChat traffic, its Plausible numbers would read
 * zero: revisit this list first.
 *
 * Mechanism: Plausible's script skips every event while
 * `localStorage.plausible_ignore === 'true'` (its "exclude yourself" switch,
 * checked per event), so setting it before the script's first pageview mutes
 * that browser. Nothing else reads the flag. The mute is re-decided on every
 * full page load (not on client-side navigations): a browser we muted that no longer matches (say this list drops
 * Snapchat to count paid Snapchat traffic) is unmuted. MUTED_BY_US marks the
 * flags we set, so a teammate's own "exclude me" flag is never cleared.
 *
 * Two copies of SPOOFED_IN_APP_USER_AGENT live outside this file. Change one,
 * change all three:
 * - PostHog: a `$raw_user_agent` not_regex rule in the project's "Filter out
 *   internal and test users" settings, which hides these visits from every chart.
 * - Tinybird: lowercased, in infra/tinybird/pipes/analytics_hits.pipe, which
 *   labels them device 'bot' for the web-analytics reports to drop. AppProviders
 *   also skips loading Tinybird's tracker for these browsers.
 *
 * Background: docs/postmortems/2026-09-21-redirect-bot-farm-affiliate-clicks.md
 */
export const SPOOFED_IN_APP_USER_AGENT =
  /iPhone OS 15_7_2.*Version\/16\.5|Safari\/60[45]\.1 Snapchat|DaumApps|NAVER\(inapp|NewsSapphire|MicroMessenger|Android [^)]*; \) AppleWebKit.*Chrome\/(?:\d{1,2}|1[0-4]\d)\.|iPhone OS 18_3.*Version\/18\.3 Mobile\/15E148 Safari\/605\.1/

export const isSpoofedInAppUserAgent = (userAgent: string): boolean =>
  SPOOFED_IN_APP_USER_AGENT.test(userAgent)

const PLAUSIBLE_IGNORE = 'plausible_ignore'
const MUTED_BY_US = 'dupe_spoofed_inapp_muted'

export function muteAnalyticsForSpoofedInAppBrowser(): void {
  if (typeof window === 'undefined') return
  try {
    const storage = window.localStorage
    const ours = storage.getItem(MUTED_BY_US) !== null
    if (isSpoofedInAppUserAgent(window.navigator.userAgent)) {
      // A flag already set by someone else stays theirs: never mark it ours.
      if (!ours && storage.getItem(PLAUSIBLE_IGNORE) === 'true') return
      // Marker first, so a failed second write can't leave an unowned mute.
      storage.setItem(MUTED_BY_US, '1')
      storage.setItem(PLAUSIBLE_IGNORE, 'true')
    } else if (ours) {
      storage.removeItem(PLAUSIBLE_IGNORE)
      storage.removeItem(MUTED_BY_US)
    }
  } catch {
    // Storage blocked: nothing to do, the visit is counted as before.
  }
}
